import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { UpdateFileUploadEntryInput, CreateFileUploadEntryInput } from 'app/api/API.service';
import { FileUploadAPIService } from 'app/api/file-upload.api.service';
import { AppState } from 'app/reducers';
import { parseErrorMessage } from 'app/utils/callstate';
import { sortBy } from 'lodash';
import { of } from 'rxjs';
import { catchError, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import {
    FileUploadActions,
    FileUploadActionTypes,
    FileUploadCancelled,
    FileUploadEntryCreateCancelled,
    FileUploadEntryCreated,
    FileUploadEntryCreateRequested,
    FileUploadEntryUpdateCancelled,
    FileUploadEntryUpdated,
    FileUploadEntryUpdateRequested,
    FileUploadLoaded,
    FileUploadRequested
} from './file-upload.actions';

@Injectable()
export class FileUploadEffects {
    @Effect()
    loadFileUpload$ = this.actions$.pipe(
        ofType<FileUploadRequested>(FileUploadActionTypes.FileUploadRequested),
        mergeMap(action => {
            // tslint:disable-next-line: no-shadowed-variable
            const { companyId, key, filter } = action.payload;
            return this.fileUploadApiService.FileUpload(companyId, key, filter).pipe(
                map(
                    items =>
                        new FileUploadLoaded({
                            companyId,
                            fileUpload: sortBy(items, 'key')
                        })
                ),
                catchError(error => {
                    const errorMessage = parseErrorMessage(error);
                    return of(new FileUploadCancelled({ companyId, errorMessage }));
                })
            );
        })
    );

    @Effect()
    updateFileUpload$ = this.actions$.pipe(
        ofType<FileUploadEntryUpdateRequested>(
            FileUploadActionTypes.FileUploadEntryUpdateRequested
        ),
        mergeMap(action => {
            const input = action.payload.entry as UpdateFileUploadEntryInput;
            return this.fileUploadApiService.UpdateFileUploadEntry(input).pipe(
                map(entry => new FileUploadEntryUpdated({ entry })),
                catchError(error => {
                    const errorMessage = parseErrorMessage(error);
                    return of(
                        new FileUploadEntryUpdateCancelled({ entry: action.payload.entry, errorMessage })
                    );
                })
            );
        })
    );

    @Effect()
    createFileUpload$ = this.actions$.pipe(
        ofType<FileUploadEntryCreateRequested>(
            FileUploadActionTypes.FileUploadEntryCreateRequested
        ),
        mergeMap(action => {
            const input = action.payload.entry as CreateFileUploadEntryInput;
            return this.fileUploadApiService.CreateFileUploadEntry(input).pipe(
                map(entry => new FileUploadEntryCreated({ entry })),
                catchError(error => {
                    const errorMessage = parseErrorMessage(error);
                    return of(
                        new FileUploadEntryCreateCancelled({ entry: action.payload.entry, errorMessage })
                    );
                })
            );
        })
    );

    constructor(
        private actions$: Actions<FileUploadActions>,
        private store: Store<AppState>,
        private fileUploadApiService: FileUploadAPIService
    ) {}
}
